<template>
  <div v-if="!isDismissed" class="notice-banner" :class="[variantClass, { 'is-dismissible': dismissible }]">
    <div class="notice-icon">
      <slot name="icon">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 8V12L14 14"/>
        </svg>
      </slot>
    </div>

    <div class="notice-content">
      <h4 v-if="$slots.title || title" class="notice-title">
        <slot name="title">{{ title }}</slot>
      </h4>
      <div class="notice-message">
        <slot>{{ message }}</slot>
      </div>
    </div>

    <button
      v-if="dismissible"
      class="notice-close-btn"
      type="button"
      aria-label="Close"
      @click.stop="handleDismiss"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
        <path d="M18 6L6 18M6 6L18 18" />
      </svg>
    </button>
  </div>
</template>

<script>
import { ref } from 'vue';

export default {
  name: 'NoticeBanner',
  props: {
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'primary' // 'primary' | 'subtle' | 'warning'
    },
    dismissible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['dismiss'],
  setup(props, { emit }) {
    const isDismissed = ref(false);

    function handleDismiss() {
      isDismissed.value = true;
      emit('dismiss');
    }

    const variantClass = `variant-${props.variant}`;

    return {
      isDismissed,
      handleDismiss,
      variantClass
    };
  }
};
</script>

<style scoped>
.notice-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  color: #4B5563;
  font-size: 12px;
  line-height: 1.4;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.notice-banner.variant-primary {
  border-color: #E5E7EB;
}

.notice-banner.variant-subtle {
  background-color: #F9FAFB;
  border: 1px solid #E5E7EB;
}

.notice-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color, #F93E3E);
  flex-shrink: 0;
}

.notice-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.notice-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--primary-color, #F93E3E);
  margin: 0;
}

.notice-message {
  font-size: 11.5px;
  color: #4B5563;
  margin: 0;
  line-height: 1.4;
}

.notice-message :deep(strong) {
  color: #111827;
  font-weight: 600;
}

.notice-close-btn {
  background: none;
  border: none;
  color: #9CA3AF;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease, background-color 0.15s ease;
  flex-shrink: 0;
  margin-left: auto;
}

.notice-close-btn:hover {
  color: #1F2937;
  background-color: #F3F4F6;
}

@media (min-width: 992px) {
  .notice-banner {
    padding: 12px 16px;
    gap: 14px;
    border-radius: 10px;
  }

  .notice-title {
    font-size: 13px;
  }

  .notice-message {
    font-size: 12px;
  }
}
</style>
